import { Component, Input, EventEmitter, Output, OnDestroy, OnInit, inject } from '@angular/core';
import {
  ControlContainer,
  FormGroupDirective,
  FormGroup,
  Validators,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, debounceTime, tap } from 'rxjs/operators';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { WarehouseStateModel } from '@sales/states/warehouse/warehouse.state';
import { MbbRouter } from '../../../../models/mbb-offerings-response.model';
import { MbbProduct } from '../../../../models/mbb-product.model';
import { DealerClaimType } from '@core/enums/dealer-claim-type';
import { IconNames } from '@shared/enums/icon-names';
import { imageAssets } from '@core/constants/themes';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { AllowSettingsDirective } from '@app/shared/directives/allow-settings.directive';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { getCorrelatedBundle } from '@app/core/utils';
import { select } from '@ngxs/store';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';

@Component({
  selector: 'app-mbb-router',
  templateUrl: './mbb-router.component.html',
  styleUrls: ['./mbb-router.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IceSelectBoxComponent,
    AllowSettingsDirective,
    SpinnerComponent,
    PriceFormatPipe,
  ],
})
export class MbbRouterComponent implements OnInit, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);

  IconNames = IconNames;
  PriceType = PriceType;
  DealerClaimType = DealerClaimType;
  imageAssets = imageAssets;
  getCorrelatedBundle = getCorrelatedBundle;
  canSellHardwareWithLogistics = select(DealerStateSelectors.canSellHardwareWithLogistics);

  @Input() mbbProduct: MbbProduct;
  @Input() mbbRouters: MbbRouter[];
  @Input() warehouseState: WarehouseStateModel;

  @Output() mbbRouterChange: EventEmitter<MbbRouter> = new EventEmitter();
  @Output() isInvalid = new EventEmitter();

  form: FormGroup;

  subscription$ = new Subscription();

  static buildChooseMbbRouterForm(mbbRouterOfferingId: string) {
    return new FormGroup({
      mbbRouter: new FormControl(mbbRouterOfferingId, [Validators.required]),
    });
  }

  ngOnInit() {
    this.form = this.buildForm();

    this.subscription$ = this.form.controls.mbbRouter.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(value => {
          if (value && this.mbbRouters.find(offering => offering.offeringId === value)) {
            this.mbbRouterChange.emit(this.mbbRouters.find(offering => offering.offeringId === value));
          }
          this.isInvalid.emit(this.form.invalid);
        })
      )
      .subscribe();

    this.isInvalid.emit(this.form.invalid);
  }

  disableRouter(router: MbbRouter): boolean {
    return router.productAvailability === 'temporaryOutOfStock';
  }

  private buildForm(): FormGroup {
    this.formGroupDirective.form.addControl(
      'chooseMbbRouter',
      MbbRouterComponent.buildChooseMbbRouterForm(this.mbbProduct?.mbbRouter?.offeringId)
    );
    return this.formGroupDirective.form.get('chooseMbbRouter') as FormGroup;
  }

  ngOnDestroy() {
    this.subscription$.unsubscribe();
  }
}
